import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, map, mergeAll, type Observable } from 'rxjs';
import type { AfterworkItemModel } from '../models/afterwork-item.model';
import type { ChristmasPartyItemModel } from '../models/christmas-party-item.model';
import type { LearningAtLunchItemModel } from '../models/learning-at-lunch-item.model';
import type { AgendaItemModel } from '../models/middleware/agenda-item.model';
import {
  type EventItem,
  type EventType,
  isEventEventWithDescriptionMarkDownField,
  isEventWithAgendaField,
  isEventWithRegistrationDeadlineField,
  isEventWithTravelInformation,
} from '../models/middleware/event-item.model';
import type { MultiItemResponse } from '../models/middleware/multi-item-response.model';
import type { OverviewItemModel } from '../models/middleware/overview-item.model';
import type { RegistrationDeadlineItemModel } from '../models/middleware/registration-deadline-item.model';
import type { NewsAtFourEnergyItemModel } from '../models/news-at-four-energy-item.model';
import type { OffsiteItemModel } from '../models/offsite-item.model';
import type { OtherEventItemModel } from '../models/other-event-item.model';
import { mapEventTypeToCategory } from '../utils/event-category.util';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  private readonly http = inject(HttpClient);
  private readonly url = `/api/authenticated/event`;

  private readonly endpoints = {
    afterwork: 'afterwork',
    offsite: 'offsite',
    christmasParty: 'christmas-party',
    learningAtLunch: 'learning-at-lunch',
    newsAtFourEnergy: 'news-at-four-energy',
    other: 'other',
  };

  private currentEvent = signal<EventItem<EventType> | null>(null);

  setCurrentEvent(event: EventItem<EventType>) {
    this.currentEvent.set(event);
  }

  getCurrentEvent() {
    return this.currentEvent();
  }

  clearCurrentEvent() {
    this.currentEvent.set(null);
  }

  getEventOverview(current?: boolean) {
    return this.http
      .get<MultiItemResponse<OverviewItemModel>>(`${this.url}/overview`, {
        params: new HttpParams().set('current', !!current),
      })
      .pipe(
        map((response) => response.items),
        catchError((error: Error) => {
          console.error(error);
          throw error;
        })
      );
  }

  getEvent(documentId: string, eventType: EventType | undefined): Observable<EventItem<EventType>> {
    switch (eventType) {
      case 'Afterwork':
        return this.getAfterworkEvent(documentId);
      case 'Offsite':
        return this.getOffsiteEvent(documentId);
      case 'Weihnachtsfeier':
        return this.getChristmasPartyEvent(documentId);
      case 'LearningAtLunch':
        return this.getLearningAtLunchEvent(documentId);
      case 'NewsAtFourEnergy':
        return this.getNewsAtFourEnergyEvent(documentId);
      case 'Other':
        return this.getOtherEvent(documentId);
      default:
        throw new Error(`Unsupported event type: ${eventType}`);
    }
  }

  getEvents(eventType: EventType): Observable<EventItem<EventType>[]> {
    switch (eventType) {
      case 'Afterwork':
        return this.getAfterworkEvents();
      case 'Offsite':
        return this.getOffsiteEvents();
      case 'Weihnachtsfeier':
        return this.getChristmasPartyEvents();
      case 'LearningAtLunch':
        return this.getLearningAtLunchEvents();
      case 'NewsAtFourEnergy':
        return this.getNewsAtFourEnergyEvents();
      case 'Other':
        return this.getOtherEvents();
      default:
        throw new Error(`Unsupported event type: ${eventType}`);
    }
  }

  getAfterworkEvent(documentId: string) {
    return this.getEventItem<AfterworkItemModel>(documentId, this.endpoints.afterwork);
  }

  getAfterworkEvents(documentIds: string[] = []) {
    return this.getEventItems<AfterworkItemModel>(documentIds, this.endpoints.afterwork);
  }

  getOffsiteEvent(documentId: string) {
    return this.getEventItem<OffsiteItemModel>(documentId, this.endpoints.offsite);
  }

  getOffsiteEvents(documentIds: string[] = []) {
    return this.getEventItems<OffsiteItemModel>(documentIds, this.endpoints.offsite);
  }

  getChristmasPartyEvent(documentId: string) {
    return this.getEventItem<ChristmasPartyItemModel>(documentId, this.endpoints.christmasParty);
  }

  getChristmasPartyEvents(documentIds: string[] = []) {
    return this.getEventItems<ChristmasPartyItemModel>(documentIds, this.endpoints.christmasParty);
  }

  getLearningAtLunchEvent(documentId: string) {
    return this.getEventItem<LearningAtLunchItemModel>(documentId, this.endpoints.learningAtLunch);
  }

  getLearningAtLunchEvents(documentIds: string[] = []) {
    return this.getEventItems<LearningAtLunchItemModel>(documentIds, this.endpoints.learningAtLunch);
  }

  getNewsAtFourEnergyEvent(documentId: string) {
    return this.getEventItem<NewsAtFourEnergyItemModel>(documentId, this.endpoints.newsAtFourEnergy);
  }

  getNewsAtFourEnergyEvents(documentIds: string[] = []) {
    return this.getEventItems<NewsAtFourEnergyItemModel>(documentIds, this.endpoints.newsAtFourEnergy);
  }

  getOtherEvent(documentId: string) {
    return this.getEventItem<OtherEventItemModel>(documentId, this.endpoints.other);
  }

  getOtherEvents(documentIds: string[] = []) {
    return this.getEventItems<OtherEventItemModel>(documentIds, this.endpoints.other);
  }

  private getEventItem<T extends EventItem<EventType>>(documentIds: string, endpoint: string) {
    return this.getEventItems<T>([documentIds], endpoint).pipe(mergeAll());
  }

  private getEventItems<T extends EventItem<EventType>>(documentIds: string[], endpoint: string) {
    let params = {};
    if (documentIds.length > 0) {
      params = { params: { documentIds: documentIds.join(',') } };
    }

    return this.http.get<MultiItemResponse<T>>(`${this.url}/${endpoint}`, params).pipe(
      map((response) => {
        const result = response.items.map((item) => this.mapToEventItem(item) as T);
        this.sortByAgendaStart(result);
        return result;
      }),
      catchError((error: Error) => {
        this.clearCurrentEvent();
        console.debug(error);
        throw error;
      })
    );
  }

  private mapToEventItem<T extends EventType>(event: EventItem<T>): EventItem<EventType> {
    return {
      ...event,
      documentId: event.documentId,
      title: event.title,
      type: event.type,
      category: event.type === 'Other' ? event.category : mapEventTypeToCategory(event.type),
      startDate: new Date(event.startDate),
      ...(!!event.endDate && { endDate: new Date(event.endDate) }),
      moodImage: event.moodImage
        ? {
            url: event.moodImage.url,
            width: event.moodImage.width ?? undefined,
            height: event.moodImage.height,
            thumbnailUrl: event.moodImage.thumbnailUrl ?? undefined,
          }
        : undefined,
      ...(isEventWithAgendaField(event) && { agenda: this.mapAgendaToDate(event.agenda) }),
      ...(isEventWithTravelInformation(event) && {
        travelInformation: event.travelInformation ?? undefined,
      }),
      ...(isEventEventWithDescriptionMarkDownField(event) && {
        description: event.description ?? undefined,
      }),
      ...(isEventWithRegistrationDeadlineField(event) && {
        registrationDeadline: {
          registrationDeadline: new Date(event.registrationDeadline.registrationDeadline),
          registrationUrl: event.registrationDeadline.registrationUrl,
        } as RegistrationDeadlineItemModel,
      }),
    };
  }

  private mapAgendaToDate(agenda: AgendaItemModel[]): AgendaItemModel[] {
    return agenda.map((agendaItem) => ({
      ...agendaItem,
      startTime: new Date(agendaItem.startTime),
      endTime: new Date(agendaItem.endTime),
    }));
  }

  private sortByAgendaStart<T extends EventItem<EventType>>(eventItem: T[]) {
    eventItem.forEach((item) => {
      if (isEventWithAgendaField(item)) {
        item.agenda.sort((a, b) => {
          return a.startTime.getTime() - b.startTime.getTime();
        });
      }
    });
  }
}
